class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;

    // UI hooks
    this.resetButton?.addEventListener('click', this.clear.bind(this));
    this.input?.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input?.addEventListener('focus', this.onFocus.bind(this));

    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';

    // Für Template-Suche (Seite /drawer)
    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  onFocus(event) {
    if (!this.closest('.template-search')) return;

    document.body.classList.add('predictive-search-open');

    if (this.getQuery().length === 0) return;

    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return (this.input?.value || '').trim();
  }

  clear(event = null) {
    if (event) event.preventDefault();

    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    if (this.input) {
      this.input.value = '';
      this.input.focus();
    }
    this.removeAttribute('results');
  }

  /**
   * Erzeugt die Predictive-Search-URL inklusive Feldern:
   *  - variants.sku  → SKU-Suche im Dropdown
   *  - tag           → Tag-Suche im Dropdown
   *  - title, variants.title, product_type, vendor → Standard
   */
  setupURL() {
    const url = new URL(`${FoxTheme.routes.shop_url}${FoxTheme.routes.predictive_search_url}`);

    // Query & Basis
    url.searchParams.set('q', this.getQuery());
    url.searchParams.set('options[prefix]', 'last'); // konsistent mit deinem <input type="hidden" name="options[prefix]">
    url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3);
    url.searchParams.set('resources[limit_scope]', 'each');

    // Welche Ressourcentypen?
    // Mindestens 'product'; weitere Typen je nach Template sinnvoll
    url.searchParams.set('resources[type]', 'product,collection,article,page');

    // WELCHE FELDER sollen durchsucht werden (entscheidend!)
    // 'variants.sku' & 'tag' machen die Suche nach SKU/Tags im Dropdown möglich.
    url.searchParams.set(
      'resources[options][fields]',
      'title,variants.title,variants.sku,product_type,tag,vendor'
    );

    // Ausverkaufte ans Ende (Optional)
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    // Section für Partial-Render
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));

    return url;
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    if (this.cachedMap.has(url)) {
      this.renderSectionFromCache(url);
    } else {
      this.renderSectionFromFetch(url);
    }
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText);
    this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    // Ladezustand anzeigen
    this.setAttribute('loading', 'true');
    this.resetButton?.classList.add('btn--loading');

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        this.removeAttribute('loading');
        this.resetButton?.classList.remove('btn--loading');
        this.setAttribute('results', 'true');
      });
  }

  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (!targetElement) {
      console.error(`Element with id '${id}' not found in the document.`);
      return;
    }

    const parser = new DOMParser();
    const parsedDoc = parser.parseFromString(responseText, 'text/html');
    const contentElement = parsedDoc.getElementById(id);

    if (contentElement) {
      this.searchContent?.classList.remove('hidden');
      targetElement.innerHTML = contentElement.innerHTML;
    } else {
      console.error(`Element with id '${id}' not found in the parsed response.`);
    }
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);
